import { useApolloClient } from '@apollo/client';
import { message } from 'antd';
import { Formik } from 'formik';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
    type CreateFinanceProductMutation,
    type CreateFinanceProductMutationVariables,
    CreateFinanceProductDocument,
} from '../../../api/mutations/createFinanceProduct';
import { FinanceProductKind } from '../../../api/types';
import useLoadingButton from '../../../components/button/useLoadingButton';
import { useCompany } from '../../../components/contexts/CompanyContextManager';
import useHandleError from '../../../utilities/useHandleError';
import { defaultValues, type FinanceProductFormValues } from '../FinanceProductDetailsPage/FinanceProductForm';
import mapFormValuesToPayload from '../FinanceProductDetailsPage/FinanceProductForm/helpers/mapFormValuesToPayload';
import useFinanceProductFormValidator from '../FinanceProductDetailsPage/FinanceProductForm/useProductFormValidator';
import useLocalVehicles, {
    useFinderVehicles,
} from '../FinanceProductDetailsPage/FinanceProductForm/VehicleAssignmentTab/useVehicles';
import Inner from './Inner';

const AddFinanceProductPage = () => {
    const { t } = useTranslation(['financeProductDetails']);
    const navigate = useNavigate();
    const validate = useFinanceProductFormValidator();
    const { setLoading, LoadingButton } = useLoadingButton();

    const apolloClient = useApolloClient();

    const company = useCompany(true);
    const vehicles = useLocalVehicles({ companyId: company?.id });
    const finderVehicles = useFinderVehicles({ companyId: company?.id });

    const onSubmit = useHandleError<FinanceProductFormValues>(
        async values => {
            setLoading(true);
            // submitting message
            message.loading({
                content: t('financeProductDetails:messages.createSubmitting'),
                key: 'primary',
                duration: 0,
            });

            // submit creation
            const { data } = await apolloClient
                .mutate<CreateFinanceProductMutation, CreateFinanceProductMutationVariables>({
                    mutation: CreateFinanceProductDocument,
                    variables: {
                        settings: mapFormValuesToPayload(values, { vehicles, finderVehicles }),
                        kind: FinanceProductKind.Local,
                    },
                })
                .finally(() => {
                    // clean up
                    message.destroy('primary');
                    setLoading(false);
                });

            // inform about success
            message.success({
                content: t('financeProductDetails:messages.createSuccessful'),
                key: 'primary',
            });

            // then go back to list
            navigate(`/admin/financeProducts/${data.product.id}`);
        },
        [apolloClient, finderVehicles, navigate, setLoading, t, vehicles]
    );

    return (
        <Formik initialValues={defaultValues} onSubmit={onSubmit} validate={validate}>
            {({ handleSubmit }) => <Inner handleSubmit={handleSubmit} loadingButton={LoadingButton} />}
        </Formik>
    );
};

export default AddFinanceProductPage;
